import React from "react";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { Route, Switch } from "react-router-dom";
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material/styles";
import configureStore from "./configureStore";
import { createHashHistory } from "history";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
/* import Main from "./Main"; */
import Login from "./pages/Login";
import Dashboard from "./pages/dashboard/Dashboard";
import User from "./pages/user/User";
import UserEdit from "./pages/user/userEdit/UserEdit";
import UserGroup from "./pages/userGroup/UserGroup";
import UserGroupEdit from "./pages/userGroup/userGroupEdit/UserGroupEdit";
import UserGroupView from "./pages/userGroup/userGroupView/UserGroupView";
import Merchant from "./pages/merchant/Merchant";
import MerchantEdit from "./pages/merchant/merchantEdit/MerchantEdit";
import Processor from "./pages/processor/Processor";
import ProcessorEdit from "./pages/processor/processorEdit/ProcessorEdit";
import Pos from "./pages/pos/Pos";
import PosEdit from "./pages/pos/posEdit/PosEdit";
import PosView from "./pages/pos/posView/PosView";
import Terminal from "./pages/terminal/Terminal";
import TerminalEdit from "./pages/terminal/terminalEdit/TerminalEdit";
import TerminalView from "./pages/terminal/terminalView/TerminalView";
import TerminalUpload from "./pages/terminal/terminalUpload/TerminalUpload";
import OnBoardMerchant from "./pages/onBoardMerchant/OnBoardMerchant";
import PosSoftware from "./pages/possoftware/PosSoftware";
import PosSoftwareEdit from "./pages/possoftware/posSoftwareEdit/PosSoftwareEdit";
import PosSoftwareView from "./pages/possoftware/posSoftwareView/PosSoftwareView";

import Cert from "./pages/cert/Cert";
import CertEdit from "./pages/cert/certEdit/CertEdit";
import Settings from "./pages/settings/Settings";
import Card from "./pages/card/Card";
import CardEdit from "./pages/card/cardEdit/CardEdit";
import "../css/app.css";
import "react-toastify/dist/ReactToastify.min.css";
import CardImport from "./pages/card/cardImport/CardImport";
import CardPrompt from "./pages/card/cardPrompt/CardPrompt";
import CardPromptEdit from "./pages/card/cardPrompt/CardPromptEdit";
import CardPromptView from "./pages/card/cardPrompt/CardPromptView";
import Audit from "./pages/audit/Audit";
import OnBoarding from "./pages/onboarding/Onboarding";
import OnboardingTable from "./pages/onboarding/onboardingTable/OnboardingTable";
import { StaticConfigWrapper } from "./common/StaticConfigProvider";
import { OrgConfigProvider } from "./common/OrgConfigProvider";
import MerchantView from "./pages/merchant/merchantView/merchantView";
import ProcessorView from "./pages/processor/processorView/ProcessorView";
import UserView from "./pages/user/userView/UserView";
import withBackButton from "./common/buttons/BackButton";
import CertView from "./pages/cert/certView/CertView";
import CardView from "./pages/card/cardView/CardView";
import BulkUpdate from "./common/bulkUpdate/BulkUpdate";
import Receipt from "./pages/htmlReceipt/Receipt";
import ReceiptEdit from "./pages/htmlReceipt/receiptEdit/ReceiptEdit";
import ReceiptView from "./pages/htmlReceipt/receiptView/ReceiptView";
import ErrorBoundary from "./common/ErrorBoundary";
import { Auth0ProviderWrapper } from "./pages/auth/Auth0ProviderWrapper";
import CardTypes from "./pages/cardType/CardType";
import CardTypeEdit from "./pages/cardType/cardTypeEdit/CardTypeEdit";
import HotCardEdit from "./pages/hashedHotCard/hashedHotCardEdit/HashedHotCardEdit";
import TerminalBackground from "./pages/terminalBackground/terminalBackground";
import { ToastContainer } from "react-toastify";
import MerchantStaging from "./pages/merchantStaging/MerchantStaging";
import MerchantStageView from "./pages/merchantStaging/merchantStageView/merchantStageView";
import HashedHotCard from "./pages/hashedHotCard/HashedHotCard";
import HashedHotCardView from "./pages/hashedHotCard/hashedHotCardView/HashedHotCardView";

export const history = createHashHistory();

const store = configureStore(history);

const theme = createTheme({
  palette: {
    primary: {
      main: "#000000", // var(--dmg-primary)
      contrastText: "#FFFFFF", // var(--dmg-bg)
    },
    secondary: {
      main: "#DA007C", // var(--dmg-purple)
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          "& legend": {
            textTransform: "uppercase",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#A9A9A9", // var(--dmg-gray-500)
          textTransform: "uppercase",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
        },
      },
    },
    // Override all MUI icons to use purple color
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: "#DA007C", // var(--dmg-purple)
        },
      },
    },
    // Override specific icon components that might have their own color props
    MuiIcon: {
      styleOverrides: {
        root: {
          color: "#DA007C", // var(--dmg-purple)
        },
      },
    },
    // Override icon buttons to use purple
    MuiIconButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#DA007C", // var(--dmg-purple)
          },
        },
      },
    },
    // Override list item icons
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#DA007C", // var(--dmg-purple)
          },
        },
      },
    },
    // Override input adornment icons
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#DA007C", // var(--dmg-purple)
          },
        },
      },
    },
  },
});

const toastConfigure = {
  limitToast: 5,
};

interface Configuration {
  environment: string;
}

export default class Root extends React.Component {
  readonly configuration: Configuration;

  constructor(props: Configuration) {
    super(props);
    this.configuration = props;
  }

  render(): React.ReactNode {
    return (
      <Auth0ProviderWrapper>
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <OrgConfigProvider>
                <StaticConfigWrapper
                  loader={async (): Promise<any> => {
                    try {
                      const response = await fetch("tooltips.json");
                      return response.json();
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                >
                  <ErrorBoundary>
                    <Switch>
                      <Route exact path="/" component={Login} />
                      <Route exact path="/dashboard" component={Dashboard} />
                      <Route exact path="/user" component={User} />
                      <Route
                        exact
                        path="/user/edit/:id"
                        component={UserEdit}
                      />
                      <Route
                        exact
                        path="/user/view/:id"
                        component={withBackButton(UserView)}
                      />
                      <Route exact path="/user/new" component={UserEdit} />

                      <Route exact path="/role" component={UserGroup} />
                      <Route
                        exact
                        path="/role/edit/:id"
                        component={UserGroupEdit}
                      />
                      <Route
                        exact
                        path="/role/view/:id"
                        component={withBackButton(UserGroupView)}
                      />
                      <Route
                        exact
                        path="/role/new"
                        component={UserGroupEdit}
                      />

                      <Route exact path="/merchant" component={Merchant} />
                      <Route
                        exact
                        path="/merchant/new"
                        component={MerchantEdit}
                      />
                      <Route
                        exact
                        path="/merchant/view/:id"
                        component={withBackButton(MerchantView)}
                      />
                      <Route
                        exact
                        path="/merchant/edit/:id"
                        component={MerchantEdit}
                      />

                      <Route exact path="/processor" component={Processor} />
                      <Route
                        exact
                        path="/processor/new"
                        component={ProcessorEdit}
                      />
                      <Route
                        exact
                        path="/processor/edit/:id"
                        component={ProcessorEdit}
                      />
                      <Route
                        exact
                        path="/processor/view/:id"
                        component={withBackButton(ProcessorView)}
                      />

                      <Route exact path="/pos" component={Pos} />
                      <Route exact path="/pos/new" component={PosEdit} />
                      <Route exact path="/pos/edit/:id" component={PosEdit} />
                      <Route
                        exact
                        path="/pos/view/:id"
                        component={withBackButton(PosView)}
                      />

                      <Route
                        exact
                        path="/possoftware"
                        component={PosSoftware}
                      />
                      <Route
                        exact
                        path="/possoftware/new"
                        component={PosSoftwareEdit}
                      />
                      <Route
                        exact
                        path="/possoftware/edit/:id"
                        component={PosSoftwareEdit}
                      />
                      <Route
                        exact
                        path="/possoftware/view/:id"
                        component={withBackButton(PosSoftwareView)}
                      />

                      <Route
                        exact
                        path="/terminal"
                        render={() => <Terminal {...this.configuration} />}
                      />
                      <Route
                        exact
                        path="/injected-terminal"
                        render={() => <Terminal isStaged={true} {...this.configuration} />}
                      />
                       <Route
                        exact
                        path="/staged-terminal"
                        render={() => <MerchantStaging />}
                      />
                       <Route
                        exact
                        path="/staged-terminal/view/:id"
                        component={withBackButton(MerchantStageView)}
                      />
                      <Route
                        exact
                        path="/terminalimport"
                        component={TerminalUpload}
                      />
                      <Route
                        exact
                        path="/terminal/new"
                        component={TerminalEdit}
                      />
                      <Route
                        exact
                        path="/terminal/edit/:id"
                        component={TerminalEdit}
                      />
                      <Route
                        exact
                        path="/injected-terminal/edit/:id"
                        component={TerminalEdit}
                      />
                      <Route
                        exact
                        path="/terminal/view/:id"
                        component={withBackButton(TerminalView)}
                      />
                      <Route
                        exact
                        path="/injected-terminal/view/:id"
                        component={withBackButton(TerminalView)}
                      />
                      <Route
                        exact
                        path="/terminal/updategroup"
                        component={TerminalEdit}
                      />
                      <Route
                        exact
                        path="/onboardmerchant"
                        component={OnBoardMerchant}
                      />

                      <Route exact path="/cert" component={Cert} />
                      <Route exact path="/cert/new" component={CertEdit} />
                      <Route
                        exact
                        path="/cert/edit/:id"
                        component={CertEdit}
                      />
                      <Route
                        exact
                        path="/cert/view/:id"
                        component={withBackButton(CertView)}
                      />

                      <Route exact path="/card" component={Card} />
                      <Route exact path="/card/new" component={CardEdit} />
                      <Route
                        exact
                        path="/card/edit/:id"
                        component={CardEdit}
                      />
                      <Route
                        exact
                        path="/card/view/:id"
                        component={withBackButton(CardView)}
                      />
                      <Route
                        exact
                        path="/card/import"
                        component={CardImport}
                      />

                      <Route
                        exact
                        path="/cardPrompt"
                        component={CardPrompt}
                      />
                      <Route
                        exact
                        path="/cardPrompt/new"
                        component={CardPromptEdit}
                      />
                      <Route
                        exact
                        path="/cardPrompt/edit/:id"
                        component={CardPromptEdit}
                      />

                      <Route
                        exact
                        path="/cardPrompt/view/:id"
                        component={withBackButton(CardPromptView)}
                      />

                      <Route exact path="/settings" component={Settings} />

                      <Route exact path="/audit" component={Audit} />
                      <Route exact path="/onboarding" component={OnboardingTable} />
                      <Route exact path="/onboarding/import" component={OnBoarding} />

                      <Route
                        exact
                        path="/bulkUpdate/:entity?"
                        component={BulkUpdate}
                      />
                      <Route exact path="/receipt" component={Receipt} />
                      <Route
                        exact
                        path="/receipt/new"
                        component={ReceiptEdit}
                      />
                      <Route
                        exact
                        path="/receipt/edit/:id"
                        component={ReceiptEdit}
                      />
                      <Route
                        exact
                        path="/receipt/view/:id"
                        component={withBackButton(ReceiptView)}
                      />
                      <Route exact path="/cardtype" component={CardTypes} />
                      <Route exact path="/cardtype/edit/:id" component={CardTypeEdit} />
                      <Route
                        exact
                        path="/cardtype/new"
                        component={CardTypeEdit}
                      />
                      <Route exact path="/terminal-background" component={TerminalBackground}/>
                      <Route
                        exact
                        path="/hotcard"
                        render={() => <HashedHotCard />}
                      />
                      <Route
                        exact
                        path="/hotcard/new"
                        component={HotCardEdit}
                      />
                      <Route exact path="/hotcard/edit/:id" component={withBackButton(HotCardEdit)} 
                      />
                      <Route
                        exact
                        path="/hotcard/view/:id"
                        component={withBackButton(HashedHotCardView)}
                      />
                    </Switch>
                    <ToastContainer limit={toastConfigure.limitToast} />
                  </ErrorBoundary>
                </StaticConfigWrapper>
                </OrgConfigProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </ConnectedRouter>
        </Provider>
      </Auth0ProviderWrapper>
    );
  }
}
